import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FilePdf, Plus } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { money, fdate, periodLabel } from "@/lib/format";
import { StatCard, StatusBadge } from "@/components/common";
import { TextField, SelectField } from "@/components/fields";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function OwnerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [transfer, setTransfer] = useState({ importe: "", referencia: "", nota: "", banco_id: "" });
  const [accounts, setAccounts] = useState([]);

  const load = () => api.get(`/owners/${id}/finances`).then((r) => setData(r.data));
  useEffect(() => { load(); api.get("/accounts").then((r) => setAccounts(r.data)); }, [id]);

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/owners/${id}/statement.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `estado_cuenta_${data.owner.nombre}.pdf`;
      link.click();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveTransfer = async () => {
    if (!transfer.importe || transfer.importe <= 0) return toast.error("Importe inválido");
    try {
      await api.post("/transfers", { owner_id: id, importe: parseFloat(transfer.importe), referencia: transfer.referencia, nota: transfer.nota, banco_id: transfer.banco_id || null });
      toast.success("Transferencia registrada");
      setOpen(false);
      setTransfer({ importe: "", referencia: "", nota: "", banco_id: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <div className="text-muted-foreground">Cargando…</div>;
  const { owner, finances: f, properties, charges } = data;

  return (
    <div data-testid="owner-detail-page">
      <Button variant="ghost" className="rounded-sm gap-2 mb-4 -ml-2" onClick={() => navigate("/propietarios")}><ArrowLeft size={16} /> Propietarios</Button>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-display font-bold tracking-tight">{owner.nombre}</h1>
          <p className="text-sm text-muted-foreground mt-1">{owner.tipo === "moral" ? "Persona moral" : "Persona física"} · RFC {owner.rfc || "—"}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-sm gap-2" data-testid="download-statement-btn" onClick={downloadPdf}><FilePdf size={16} /> Estado de cuenta (PDF)</Button>
          <Button className="rounded-sm gap-2" data-testid="new-transfer-btn" onClick={() => setOpen(true)}><Plus size={16} /> Registrar transferencia</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <StatCard label="Rentas cobradas" value={money(f.rentas_cobradas)} tone="accent" />
        <StatCard label="Comisión inmobiliaria" value={money(f.comision_inmobiliaria)} tone="primary" />
        <StatCard label="Neto generado" value={money(f.neto_generado)} />
        <StatCard label="Transferido" value={money(f.transferido)} />
        <StatCard label="Saldo por transferir" value={money(f.saldo_disponible)} tone="warn" testId="owner-saldo" />
        <StatCard label="Rentas pendientes" value={money(f.rentas_pendientes)} tone="danger" />
      </div>

      <Tabs defaultValue="estado">
        <TabsList className="rounded-sm">
          <TabsTrigger value="estado" data-testid="tab-estado">Estado de cuenta</TabsTrigger>
          <TabsTrigger value="info">Información</TabsTrigger>
          <TabsTrigger value="inmuebles">Inmuebles</TabsTrigger>
          <TabsTrigger value="transferencias" data-testid="tab-transferencias">Transferencias</TabsTrigger>
        </TabsList>

        <TabsContent value="estado" className="mt-4">
          <div className="bg-card border border-border rounded-sm p-6 max-w-xl">
            <h3 className="font-display font-bold mb-4">Cálculo del importe a transferir</h3>
            <div className="space-y-2 text-sm">
              {[["Renta cobrada", f.rentas_cobradas], ["(−) Comisión inmobiliaria", -f.comision_inmobiliaria], ["(=) Neto para el propietario", f.neto_generado], ["(−) Transferencias realizadas", -f.transferido]].map(([l, v]) => (
                <div key={l} className="flex justify-between border-b border-border pb-1.5"><span>{l}</span><span className="tabular-nums">{money(v)}</span></div>
              ))}
              <div className="flex justify-between pt-2 font-bold text-base"><span>= Saldo disponible por transferir</span><span className="tabular-nums text-primary">{money(f.saldo_disponible)}</span></div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-sm overflow-hidden mt-4">
            <Table>
              <TableHeader><TableRow><TableHead>Periodo</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Importe</TableHead><TableHead className="text-right">Comisión</TableHead><TableHead className="text-right">Neto</TableHead></TableRow></TableHeader>
              <TableBody>
                {charges.map((c) => (
                  <TableRow key={c.id}><TableCell>{periodLabel(c.periodo)}</TableCell><TableCell><StatusBadge estado={c.estado} /></TableCell><TableCell className="text-right tabular-nums">{money(c.importe)}</TableCell><TableCell className="text-right tabular-nums">{money(c.comision_importe)}</TableCell><TableCell className="text-right tabular-nums">{money(c.neto_propietario)}</TableCell></TableRow>
                ))}
                {charges.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">Sin rentas registradas</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="info" className="mt-4">
          <div className="bg-card border border-border rounded-sm p-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm max-w-2xl">
            {[["Teléfono", owner.telefono], ["Correo", owner.email], ["Dirección", owner.direccion], ["Banco", owner.banco], ["Cuenta", owner.cuenta], ["CLABE", owner.clabe]].map(([l, v]) => (
              <div key={l}><div className="text-xs text-muted-foreground uppercase tracking-wide">{l}</div><div className="font-medium">{v || "—"}</div></div>
            ))}
            {owner.notas && <div className="sm:col-span-2"><div className="text-xs text-muted-foreground uppercase tracking-wide">Notas</div><div>{owner.notas}</div></div>}
          </div>
        </TabsContent>

        <TabsContent value="inmuebles" className="mt-4">
          <div className="bg-card border border-border rounded-sm overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Inmueble</TableHead><TableHead>Tipo</TableHead><TableHead>Dirección</TableHead><TableHead>Estado</TableHead></TableRow></TableHeader>
              <TableBody>
                {properties.map((p) => (<TableRow key={p.id}><TableCell className="font-medium">{p.nombre}</TableCell><TableCell className="capitalize">{p.tipo}</TableCell><TableCell>{p.direccion || "—"}</TableCell><TableCell><StatusBadge estado={p.estado} /></TableCell></TableRow>))}
                {properties.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">Sin inmuebles vinculados</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="transferencias" className="mt-4">
          <div className="bg-card border border-border rounded-sm overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Fecha</TableHead><TableHead>Referencia</TableHead><TableHead>Nota</TableHead><TableHead className="text-right">Importe</TableHead></TableRow></TableHeader>
              <TableBody>
                {f.transferencias.map((t) => (<TableRow key={t.id}><TableCell>{fdate(t.fecha)}</TableCell><TableCell>{t.referencia || "—"}</TableCell><TableCell>{t.nota || "—"}</TableCell><TableCell className="text-right tabular-nums">{money(t.importe)}</TableCell></TableRow>))}
                {f.transferencias.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">Sin transferencias</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle>Registrar transferencia</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Saldo disponible: <span className="font-bold tabular-nums">{money(f.saldo_disponible)}</span></p>
          <div className="grid gap-3 mt-2">
            <TextField label="Importe" type="number" value={transfer.importe} onChange={(v) => setTransfer((t) => ({ ...t, importe: v }))} testId="transfer-importe" />
            <TextField label="Referencia" value={transfer.referencia} onChange={(v) => setTransfer((t) => ({ ...t, referencia: v }))} testId="transfer-referencia" />
            <SelectField label="Cuenta de egreso (opcional)" value={transfer.banco_id} onChange={(v) => setTransfer((t) => ({ ...t, banco_id: v }))} testId="transfer-banco" options={accounts.map((a) => ({ value: a.id, label: `${a.nombre} (${a.proposito})` }))} />
            <TextField label="Nota" value={transfer.nota} onChange={(v) => setTransfer((t) => ({ ...t, nota: v }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="rounded-sm" data-testid="save-transfer-btn" onClick={saveTransfer}>Registrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
